import { v4 as uuid } from '@lukeed/uuid'

import { getRollbackExternalActionPath } from 'components/scenarios/utils/graph/metadata'
import {
  getNodeAtPath,
  getNodeParentPath
} from 'components/scenarios/utils/graph/nodes/getters'
import {
  isContinuousStatusCheckNode,
  isExternalActionNode,
  isGraphNode,
  isRollbackExternalActionNode
} from 'components/scenarios/utils/graph/nodes/typeguards'
import {
  type ExternalActionNode,
  type RollbackExternalActionNode,
  type ScenarioGraph,
  ScenarioGraphNodeType
} from 'types/api/scenarios/scenario'

import type {
  GraphNodeAtPath,
  GraphNodePath
} from 'components/scenarios/utils/graph/types'

export function updateExternalActionNodeIds<
  T extends ExternalActionNode | RollbackExternalActionNode
>(nodes: T[]): T[] {
  const newNodes = [...nodes]

  nodes.forEach((node, index) => {
    newNodes[index] = {
      ...node,
      id: String(index)
    }
  })

  return newNodes
}

export function upsertRollbackExternalActionNode(
  graph: ScenarioGraph,
  externalActionNode: ExternalActionNode
) {
  if (!externalActionNode) return graph

  const newRollbackExternalActionNodeBase: RollbackExternalActionNode = {
    ...externalActionNode,
    referenceExternalActionId: externalActionNode.guid,
    type: ScenarioGraphNodeType.RollbackExternalAction
  }

  // Find corresponding rollback external action node path
  const rollbackNodeIndex = graph.external_rollbacks?.findIndex(
    (n) => n.referenceExternalActionId === externalActionNode.guid
  )

  // If a corresponding rollback node exists, it needs to be updated
  if (typeof rollbackNodeIndex === 'number' && rollbackNodeIndex > -1) {
    // Find the existing rollback node
    const rollbackNodePath = getRollbackExternalActionPath(rollbackNodeIndex)

    const existingRollbackNode = getNodeAtPath(
      graph,
      rollbackNodePath
    ) as RollbackExternalActionNode

    // Replace the existing rollback node with the new configuration
    return replaceGraphNodeAtPath(
      graph,
      {
        ...newRollbackExternalActionNodeBase,
        id: String(rollbackNodeIndex),
        guid: existingRollbackNode?.guid ?? uuid()
      },
      rollbackNodePath
    )
  }

  const externalRollbacks = [...graph.external_rollbacks] ?? []

  // If a corresponding rollback node does not exist, add one that corresponds to the new external action node,
  // reverse indexed in order to maintain the appropriate rollback process.

  // Unshift the new node into the externalRollbacks list
  externalRollbacks.unshift({
    ...newRollbackExternalActionNodeBase,
    id: '0',
    guid: uuid()
  })

  // Shift the rollback node id values to match the indices of the externalRollbacks array
  graph.external_rollbacks = updateExternalActionNodeIds(externalRollbacks)

  return graph
}

function replaceTopLevelGraphNodeAtPath(
  graph: ScenarioGraph,
  node: GraphNodeAtPath,
  nodePath: GraphNodePath
) {
  const newNodeKey = String(nodePath[1])

  if (!newNodeKey) return graph

  if (nodePath[0] === 'observers' && isContinuousStatusCheckNode(node)) {
    if (!graph.observers) {
      graph.observers = [node]
    } else {
      graph.observers[newNodeKey] = node
    }
  } else if (nodePath[0] === 'external_actions' && isExternalActionNode(node)) {
    if (!graph.external_actions) {
      graph.external_actions = [node]
    } else {
      graph.external_actions[newNodeKey] = node
    }
  } else if (
    nodePath[0] === 'external_rollbacks' &&
    isRollbackExternalActionNode(node)
  ) {
    if (!graph.external_rollbacks) {
      graph.external_rollbacks = [node]
    } else {
      graph.external_rollbacks[newNodeKey] = node
    }
  } else if (nodePath[0] === 'nodes' && isGraphNode(node)) {
    if (!graph.nodes) {
      graph.nodes = { [newNodeKey]: node }
    } else {
      graph.nodes[newNodeKey] = node
    }
  }

  return graph
}

export function replaceGraphNodeAtPath(
  graph: ScenarioGraph,
  node: GraphNodeAtPath,
  nodePath: GraphNodePath
): ScenarioGraph {
  if (!node) {
    return graph
  }

  // short circuit top level replacement
  if (nodePath.length === 1) {
    graph[nodePath[0]] = node
    return graph
  }

  // short circuit top-level graph paths that have no parent
  if (nodePath.length < 3) {
    return replaceTopLevelGraphNodeAtPath(graph, node, nodePath)
  }

  const parentPath = getNodeParentPath(nodePath)
  const parent = getNodeAtPath(graph, parentPath)

  if (parent) {
    const key = String(nodePath.at(-1))

    if (key) {
      parent[key] = node
    }
  }

  return graph
}

function deleteTopLevelGraphNodeAtPath(
  graph: ScenarioGraph,
  nodePath: GraphNodePath
) {
  const nodeKey = String(nodePath[1])

  if (!nodeKey) return graph

  if (nodePath[0] === 'observers') {
    graph.observers?.splice(Number(nodeKey), 1)
  } else if (nodePath[0] === 'external_actions') {
    graph.external_actions?.splice(Number(nodeKey), 1)
  } else if (nodePath[0] === 'external_rollbacks') {
    graph.external_rollbacks?.splice(Number(nodeKey), 1)
  } else if (nodePath[0] === 'nodes') {
    delete graph.nodes[nodeKey]
  }

  return graph
}

/**
 * Delete a node from the graph at the supplied path
 */
export function deleteGraphNodeAtPath(
  graph: ScenarioGraph,
  nodePath: GraphNodePath
): ScenarioGraph {
  // short circuit top-level graph paths that have no parent
  if (nodePath.length < 3) {
    return deleteTopLevelGraphNodeAtPath(graph, nodePath)
  }

  const parentPath = getNodeParentPath(nodePath)
  const parent = getNodeAtPath(graph, parentPath)

  if (parent) {
    const key = String(nodePath.at(-1))

    if (key) {
      if (Array.isArray(parent)) {
        parent.splice(Number(key), 1)
      } else {
        delete parent[key]
      }
    }
  }

  return graph
}
